import { FormEvent, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined'

import { AuthenticationApi } from '../../clients'
import { getConfiguration } from '../../clients/configuration'
import { AppAlert } from '../../components/AppAlerts/AppAlerts'
import { addAlertWithTimeout } from '../../redux/features/alerts/alertsHelpers'
import { useAppDispatch } from '../../redux/hooks'
import { RecaptchaService } from '../../services/recaptchaService'

export const ForgotPassword = () => {

    const dispatch = useAppDispatch()
    const [email, setEmail] = useState<string>('')
    const [emailError, setEmailError] = useState<string>('')
    const [submitting, setSubmitting] = useState<boolean>(false)

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (handleEmailBlur()) {
            return
        }
        const recaptchaService = new RecaptchaService()
        const token = await recaptchaService.getToken()
            .catch(() => '')
        setSubmitting(true)
        const configuration = await getConfiguration()
        const authenticationAPI = new AuthenticationApi(configuration)
        await authenticationAPI.sendForgotPasswordEmail({
            sendForgotPasswordEmailRequest: {
                email: email,
                recaptchaToken: token,
            }
        }).then(() => {
            const appAlert = new AppAlert('success', 'Sent forgot password email', false)
            addAlertWithTimeout(dispatch, appAlert)
        }).catch(() => {
            const appAlert = new AppAlert('error', 'Something went wrong, try again later', true)
            addAlertWithTimeout(dispatch, appAlert)
        })
        setSubmitting(false)
    }

    const handleEmailFocus = () => {
        setEmailError('')
    }

    const handleEmailBlur = (): string => {
        if (!email) {
            return ''
        }
        const emailRegex = new RegExp(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g)
        if (!email.match(emailRegex)?.length) {
            const err = 'Invalid email address'
            setEmailError(err)
            return err
        }
        return ''
    }

    return (
        <Box sx={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', py: 4 }}>
            <Container component='div' maxWidth='sm'>
                <Paper elevation={3} sx={{ borderRadius: 2 }}>
                    <Card>
                        <CardContent sx={{ p: 4 }}>
                            <Stack spacing={3} alignItems='center'>
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: 2,
                                        bgcolor: 'primary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <LockResetOutlinedIcon sx={{ color: 'white', fontSize: 32 }} />
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography component="h1" variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                                        Reset Password
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        Enter your email to receive a password reset link
                                    </Typography>
                                </Box>
                                <Box component='form' onSubmit={handleSubmit} sx={{ width: '100%' }}>
                                    <Stack spacing={2.5}>
                                        <TextField
                                            required
                                            fullWidth
                                            value={email}
                                            error={!!emailError}
                                            helperText={emailError}
                                            onFocus={handleEmailFocus}
                                            onBlur={handleEmailBlur}
                                            onChange={(e) => setEmail(e.target.value)}
                                            label='Email'
                                            autoComplete='email'
                                            autoCapitalize='off'
                                            autoCorrect='off'
                                            autoFocus
                                        />
                                        <Button
                                            disabled={submitting || !!emailError || !email}
                                            type='submit'
                                            fullWidth
                                            variant="contained"
                                            size="large"
                                        >
                                            {submitting ? 'Sending...' : 'Send Reset Link'}
                                        </Button>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant='caption' color='text.secondary'>
                                                This site is protected by reCAPTCHA and the Google&nbsp;
                                                <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener">Privacy Policy</Link> and&nbsp;
                                                <Link href="https://policies.google.com/terms" target="_blank" rel="noopener">Terms of Service</Link> apply.
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                                            <Typography variant='body2' color='text.secondary'>
                                                Remember your password?{' '}
                                                <RouterLink to='/auth/sign-in' style={{ textDecoration: 'none' }}>
                                                    <Link component='span' sx={{ fontWeight: 600 }}>Sign in</Link>
                                                </RouterLink>
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Paper>
            </Container>
        </Box>
    )
}